import { RefObject, useLayoutEffect, useRef } from "react";

/** The design system's `--ease-standard`. Even-paced, so a short fade uses the time it is given. */
export const EASE_STANDARD = "cubic-bezier(0.4, 0, 0.2, 1)";

/**
 * Ease-out-cubic. The design system's `--ease-premium` (`cubic-bezier(0.16, 1, 0.3, 1)`) is tuned
 * for short durations and is near-vertical out of the gate: stretched much past its own
 * `--duration-page`, it finishes the visible movement inside the first third and spends the rest
 * creeping through its last pixel, which reads as lag rather than languor. This curve is ~70%
 * travelled by a third of the duration instead of ~90%, so the motion spreads across whatever
 * duration it is given.
 */
export const EASE_OUT_CUBIC = "cubic-bezier(0.33, 1, 0.68, 1)";

const PREFERS_REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

export interface ContentEnterTransition {
    /**
     * How far the content rises, as a CSS length.
     *
     * Keep this within whatever bottom breathing room sits between the element and the end of its
     * nearest scroll container. A transformed box still contributes to that container's scrollable
     * overflow, so content that starts lower than the slack below it extends past the container's
     * bottom and raises a scrollbar for the length of the transition — on any view whose content
     * already fills the viewport, every transition would flash one.
     */
    travel: string;
    glideDurationMs: number;
    glideEasing: string;
    /** Shorter than the glide, so the fade lands while the glide is still easing into place. */
    fadeDurationMs: number;
    fadeEasing: string;
}

/**
 * A page-scale entrance (design system §6 — Motion), shared by the authenticated layout and the
 * public shell so a navigation feels the same on either side of the login.
 *
 * The duration deliberately runs at twice the design system's `--duration-page` (300ms) for a more
 * languid entrance, which is also why the glide is off `--ease-premium` — see EASE_OUT_CUBIC.
 *
 * The travel doubles as the bottom slack a page-scale surface has to leave below its content, for
 * the scrollable-overflow reason on `travel` above. Measured in Chrome against both shells: at 2rem
 * neither grows its scroll extent on a page that fits the viewport; at 56px both do.
 */
export const PAGE_TRANSITION: ContentEnterTransition = {
    travel: "2rem",
    glideDurationMs: 600,
    glideEasing: EASE_OUT_CUBIC,
    fadeDurationMs: 440,
    fadeEasing: EASE_STANDARD,
};

interface ContentEnterTransitionOptions {
    /** False parks the hook — e.g. a tab panel that is not the visible one. */
    enabled?: boolean;
    /** Skip the run that happens on mount, so only genuine changes animate. */
    skipInitial?: boolean;
}

/**
 * Fades and glides an element up into place each time `trigger` changes, and hands back the ref to
 * attach to it.
 *
 * Enter-only by design: holding the outgoing content mounted for an exit animation would
 * double-render the heavy grid views and add latency to every change.
 *
 * It animates the element imperatively rather than remounting a keyed wrapper, because the element
 * has to survive the change — pages reach into #main-container by id and size themselves against it
 * (see ProductGroupManagement), and an extra wrapper div would break the `height: 100%` that
 * content measures against it. Neither animation fills, so opacity and transform are back to their
 * base values the moment they end and the element never lingers as a containing block for
 * `position: fixed` descendants.
 */
function useContentEnterTransition<T extends HTMLElement>(
    trigger: unknown,
    transition: ContentEnterTransition,
    { enabled = true, skipInitial = false }: ContentEnterTransitionOptions = {},
): RefObject<T | null> {
    const ref = useRef<T>(null);
    const isFirstRunRef = useRef(true);
    const { travel, glideDurationMs, glideEasing, fadeDurationMs, fadeEasing } = transition;

    useLayoutEffect(() => {
        // Ahead of every other guard: a parked run still counts as the mount run, so a tab panel
        // that is not the initially selected one still animates the first time it is opened.
        const isFirstRun = isFirstRunRef.current;
        isFirstRunRef.current = false;

        if (!enabled || (skipInitial && isFirstRun)) {
            return undefined;
        }

        const node = ref.current;

        if (!node || typeof node.animate !== "function") {
            return undefined;
        }

        if (window.matchMedia?.(PREFERS_REDUCED_MOTION)?.matches) {
            return undefined;
        }

        // These are precisely the frames in which the incoming content does its heaviest layout and
        // paint work — mounting a grid, most of all. Promote the element up front so the compositor
        // owns the transition: without this the first frame pays for the promotion the opacity
        // animation forces anyway, and it lands as a stutter right at the start.
        node.style.willChange = "opacity, transform";
        const settle = () => {
            node.style.willChange = "";
        };

        const fade = node.animate([{ opacity: 0 }, { opacity: 1 }], { duration: fadeDurationMs, easing: fadeEasing });
        const glide = node.animate([{ transform: `translateY(${travel})` }, { transform: "translateY(0)" }], {
            duration: glideDurationMs,
            easing: glideEasing,
        });

        // The glide outlasts the fade, so it owns the hand-back to the base style.
        glide.addEventListener("finish", settle);
        glide.addEventListener("cancel", settle);

        // Clicking through faster than the transition runs would otherwise stack animations on the
        // element; cancelling drops the in-flight one (and clears the promotion) before the next
        // effect starts its own.
        return () => {
            fade.cancel();
            glide.cancel();
        };
    }, [trigger, enabled, skipInitial, travel, glideDurationMs, glideEasing, fadeDurationMs, fadeEasing]);

    return ref;
}

export default useContentEnterTransition;
